import yup from 'utils/yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useContext, useState, useEffect } from 'react';
import { diContainerContext } from 'application/contexts/useDiContainer';
import { CompanyDetailQuery } from 'interfaceAdapter/queries/CompanyDetail';
import { AgentRequestMeetingRepository } from 'interfaceAdapter/repositories/AgentRequestMeeting';
import { CompanyRegistrationProfileInfoQuery } from 'interfaceAdapter/queries/CompanyRegistrationProfileInfo';
import { IPublicCompany } from 'domain/entities/Company/Company';
import { IIndustry } from 'domain/entities/Industry/Industry';
import { useModal } from 'ui/components/screens/modals/useModal';
import { useParams, useNavigate } from 'react-router-dom';
import { agentUserContext } from 'application/contexts/useAgentUser';
import { useLoading } from 'application/contexts/useLoading';

const schema = yup
    .object({
        message: yup.string().max(1000),
        premium_offer: yup.boolean().required(),
    })
    .required();

type RequestMeetingForm = yup.InferType<typeof schema>;

export const useCompanyDetail = () => {
    const diContainer = useContext(diContainerContext);
    const companyDetailQuery = diContainer.resolve(CompanyDetailQuery);
    const agentRequestMeetingRepository = diContainer.resolve(AgentRequestMeetingRepository);
    const companyRegistrationProfileInfoQuery = diContainer.resolve(CompanyRegistrationProfileInfoQuery);
    const { loading, setLoading } = useLoading();
    const [areaChoices, setAreaChoices] = useState<string[][]>([]);
    const [prefectureChoices, setPrefectureChoices] = useState<string[][]>([]);
    const [employeeChoices, setEmployeeChoices] = useState<string[][]>([]);
    const [revenueChoices, setRevenueChoices] = useState<string[][]>([]);
    const [industryChoices, setIndustryChoices] = useState<IIndustry[]>([]);
    const [profitLevelChoices, setProfitLevelChoices] = useState<string[][]>([]);
    const [netAssetsLevelChoices, setNetAssetsLevelChoices] = useState<string[][]>([]);
    const [company, setCompany] = useState<IPublicCompany>();
    const [showSnackbar, setShowSnackbar] = useState(false);
    const { user } = useContext(agentUserContext);
    const params = useParams();
    const sendRequestModal = useModal();
    const needProfileModal = useModal();
    const navigate = useNavigate();

    const {
        handleSubmit,
        formState: { errors },
        setValue,
        watch,
    } = useForm<RequestMeetingForm>({
        resolver: yupResolver(schema),
        mode: 'onChange',
        defaultValues: {
            message: '',
            premium_offer: false,
        },
    });

    useEffect(() => {
        if (params.company_id === undefined) {
            return;
        }

        const fetchData = async () => {
            setLoading(true);
            try {
                const resCompanies = await companyDetailQuery.fetch(Number(params.company_id));
                setCompany(resCompanies);
            } finally {
                setLoading(false);
            }
        };
        fetchData();

        const fetchProfileInfoData = async () => {
            const data = await companyRegistrationProfileInfoQuery.fetch();
            setAreaChoices(data.area_choices);
            setPrefectureChoices(data.prefecture_choices);
            setEmployeeChoices(data.employee_choices);
            setRevenueChoices(data.revenue_choices);
            setIndustryChoices(data.industries);
            setProfitLevelChoices(data.profit_level_choices);
            setNetAssetsLevelChoices(data.net_assets_level_choices);
        };
        fetchProfileInfoData();
    }, []);

    const submit = handleSubmit(async (data) => {
        try {
            setLoading(true);
            await agentRequestMeetingRepository.postToCompany(
                data.message ?? '',
                data.premium_offer,
                Number(params.company_id),
            );
            sendRequestModal.closeModal();
            setShowSnackbar(true);
            setTimeout(() => {
                setShowSnackbar(false);
                navigate('/agent/offer/');
            }, 2000);
        } catch (e) {
            throw e;
        } finally {
            setLoading(false);
        }
    });

    return {
        user,
        loading,
        company,
        setValue,
        submit,
        watch,
        sendRequestModal,
        errors,
        showSnackbar,
        needProfileModal,
        setShowSnackbar,
        areaChoices,
        prefectureChoices,
        employeeChoices,
        revenueChoices,
        industryChoices,
        profitLevelChoices,
        netAssetsLevelChoices,
    };
};
